/**
 * Image utilities for handling different image formats from the API
 */

// Maximum file size in bytes (3MB)
export const MAX_IMAGE_SIZE = 3 * 1024 * 1024;

/**
 * Normalizes a filename to remove accents and special characters
 * This prevents encoding issues when uploading files via FormData
 */
export function normalizeFileName(fileName: string): string {
  // Get the extension
  const lastDotIndex = fileName.lastIndexOf('.');
  const name = lastDotIndex > 0 ? fileName.substring(0, lastDotIndex) : fileName;
  const extension = lastDotIndex > 0 ? fileName.substring(lastDotIndex) : '';

  // Normalize unicode characters and remove accents
  const normalized = name
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Remove diacritical marks (accents)
    .replace(/['']/g, '') // Remove apostrophes
    .replace(/[^\w\s.-]/g, '') // Remove special characters except word chars, spaces, dots, hyphens
    .replace(/\s+/g, '_') // Replace spaces with underscores
    .replace(/_+/g, '_') // Replace multiple underscores with single
    .replace(/^_|_$/g, ''); // Remove leading/trailing underscores

  return (normalized || 'image') + extension.toLowerCase();
}

/**
 * Creates a new File with a normalized filename
 */
export function normalizeFile(file: File): File {
  const normalizedName = normalizeFileName(file.name);
  if (normalizedName === file.name) {
    return file; // No change needed
  }
  return new File([file], normalizedName, {
    type: file.type,
    lastModified: file.lastModified,
  });
}

// Allowed image MIME types (alignés avec UPLOAD_ALLOWED_IMAGE_MIME_TYPES côté serveur)
export const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

// Maximum dimensions for images
export const MAX_IMAGE_DIMENSION = 1920;

// Compression options
export interface CompressionOptions {
  maxSizeMB?: number;
  maxWidthOrHeight?: number;
  quality?: number;
}

/**
 * Compresses an image file to ensure it's under the max size
 * Uses canvas to resize and compress the image
 */
export async function compressImage(
  file: File,
  options: CompressionOptions = {}
): Promise<File> {
  const {
    maxSizeMB = 3,
    maxWidthOrHeight = MAX_IMAGE_DIMENSION,
    quality = 0.85,
  } = options;

  const maxSizeBytes = maxSizeMB * 1024 * 1024;

  // Le serveur accepte JPEG/PNG/WebP (configurable via UPLOAD_ALLOWED_IMAGE_MIME_TYPES).
  // Pas de recompression si le fichier est déjà sous la limite de taille.
  if (file.size <= maxSizeBytes) {
    return file;
  }

  return new Promise((resolve, reject) => {
    const img = new Image();
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      reject(new Error('Could not get canvas context'));
      return;
    }

    img.onload = () => {
      // Calculate new dimensions
      let { width, height } = img;

      if (width > maxWidthOrHeight || height > maxWidthOrHeight) {
        if (width > height) {
          height = Math.round((height * maxWidthOrHeight) / width);
          width = maxWidthOrHeight;
        } else {
          width = Math.round((width * maxWidthOrHeight) / height);
          height = maxWidthOrHeight;
        }
      }

      canvas.width = width;
      canvas.height = height;

      // Draw image on canvas
      ctx.drawImage(img, 0, 0, width, height);

      // Try to compress with progressively lower quality
      const compress = (currentQuality: number) => {
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Could not compress image'));
              return;
            }

            // If still too large and quality can be reduced, try again
            if (blob.size > maxSizeBytes && currentQuality > 0.3) {
              compress(currentQuality - 0.1);
              return;
            }

            // Renomme en .jpg puisque la sortie canvas est forcée en image/jpeg
            const jpegName = file.name.replace(/\.[^.]+$/, '') + '.jpg';
            const compressedFile = new File([blob], jpegName, {
              type: 'image/jpeg',
              lastModified: Date.now(),
            });

            resolve(compressedFile);
          },
          'image/jpeg',
          currentQuality
        );
      };

      compress(quality);
    };

    img.onerror = () => {
      reject(new Error('Could not load image'));
    };

    // Load the image
    const reader = new FileReader();
    reader.onload = (e) => {
      img.src = e.target?.result as string;
    };
    reader.onerror = () => {
      reject(new Error('Could not read file'));
    };
    reader.readAsDataURL(file);
  });
}

/**
 * Compresses multiple images in parallel
 */
export async function compressImages(
  files: File[],
  options: CompressionOptions = {}
): Promise<File[]> {
  return Promise.all(files.map((file) => compressImage(file, options)));
}

/**
 * Validates if an image file is within acceptable parameters
 */
export function validateImageFile(file: File): { valid: boolean; error?: string } {
  // Check file type (only JPEG, PNG, WebP allowed)
  if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
    return { valid: false, error: 'Format non supporté. Utilisez JPEG, PNG ou WebP.' };
  }

  // Check file size (before compression, max 10MB as upload limit)
  if (file.size > 10 * 1024 * 1024) {
    return { valid: false, error: 'Image trop volumineuse (max 10 Mo avant compression)' };
  }

  return { valid: true };
}

// Image object structure from the server
export interface ImageObject {
  id: number;
  url: string;
  order: number;
}

// Type for image that can be either a string URL or an ImageObject
export type ImageInput = string | ImageObject | null | undefined;
export type ImagesInput = ImageInput[] | null | undefined;

/**
 * Extracts the URL from an image that can be either a string or an ImageObject
 */
export function getImageUrl(image: ImageInput): string | undefined {
  if (!image) return undefined;
  if (typeof image === 'string') return image;
  if (typeof image === 'object' && 'url' in image) return image.url;
  return undefined;
}

/**
 * Normalizes an array of images to an array of URLs
 */
export function normalizeImages(images: ImagesInput): string[] {
  if (!images || !Array.isArray(images)) return [];
  return images
    .map(getImageUrl)
    .filter((url): url is string => url !== undefined);
}

/**
 * Gets the first image URL from an array of images
 */
export function getFirstImageUrl(images: ImagesInput): string | undefined {
  if (!images || !Array.isArray(images) || images.length === 0) return undefined;
  return getImageUrl(images[0]);
}

/**
 * Sorts images by their order property (if available)
 */
export function sortImagesByOrder(images: ImagesInput): ImageObject[] {
  if (!images || !Array.isArray(images)) return [];

  const imageObjects = images.filter(
    (img): img is ImageObject =>
      typeof img === 'object' && img !== null && 'url' in img
  );

  return imageObjects.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
}

/**
 * Gets the logo/main image URL for a shop from various possible sources:
 * - shop.image?.url (from list API)
 * - shop.logo (direct string URL)
 * - shop.images?.[0] (first image from detail API)
 */
export function getShopLogoUrl(shop: {
  image?: { url?: string } | null;
  logo?: string | null;
  images?: ImagesInput;
}): string | undefined {
  console.log('Getting shop logo URL for shop:', shop);
  // Priority 1: image object from list API
  if (shop.image?.url) return shop.image.url;
  // Priority 2: logo string
  if (shop.logo) return shop.logo;
  // Priority 3: first image from images array
  return getFirstImageUrl(shop.images);
}
